import type { GroupChannel } from "@sendbird/chat/groupChannel";
import { DateTime } from "luxon";
import type { InternalOpsChannel } from "@/lib/api/internal-ops-messaging";
import type { MentorshipDetails } from "@/lib/api/mentorships";
import type { D7TriggerQaScenario } from "@/lib/hooks/admin/use-dev-overrides";
import type { DismissedKindsByMentorship } from "../action/context/use-action-suggestion-dismissals";
import {
  buildMenteeAvatarByUrl,
  toNoticeConversations,
} from "./notice-conversation-adapter";
import type { NoticeConversation } from "./notice-conversations";
import { buildMentorshipSignalsByUrl } from "./notice-mentorship-signals";
import { sortNoticeConversations } from "./notice-sort";
import {
  groupSupportConversations,
  type NoticeSupportGroups,
} from "./notice-support-grouping";
import { getNoticeWorkspaceBehavior } from "./notice-workspace";
import type { MentorWorkspaceId } from "./workspace";

export type NoticeView =
  | { kind: "flat"; conversations: NoticeConversation[] }
  | {
      kind: "grouped";
      groups: NoticeSupportGroups;
      conversations: NoticeConversation[];
    };

export interface NoticeViewerContext {
  currentUserId: string;
  isCurrentUserMentor: boolean;
  activeSupervisorId: string | null;
}

export interface NoticeViewInputs {
  workspace: MentorWorkspaceId;
  channels: GroupChannel[];
  mentorships: MentorshipDetails[];
  internalOpsChannels: InternalOpsChannel[];
  viewerContext: NoticeViewerContext;
  channelsLoading: boolean;
  channelsError: string | null;
  internalLoading: boolean;
  internalError: string | null;
  qaScenario?: D7TriggerQaScenario | null;
  dismissedByMentorship?: DismissedKindsByMentorship;
}

export interface NoticeViewResult {
  view: NoticeView;
  loading: boolean;
  error: string | null;
}

export function buildNoticeView(
  inputs: NoticeViewInputs,
  now: DateTime = DateTime.now(),
): NoticeViewResult {
  const behavior = getNoticeWorkspaceBehavior(inputs.workspace);
  const { currentUserId, isCurrentUserMentor, activeSupervisorId } =
    inputs.viewerContext;

  const mentorshipByUrl = buildMentorshipSignalsByUrl(inputs.mentorships, {
    now,
    qaScenario: inputs.qaScenario ?? null,
    dismissedByMentorship: inputs.dismissedByMentorship,
  });

  const startedAtByUrl = new Map(
    inputs.mentorships.flatMap((mentorship) =>
      mentorship.created_at
        ? [[mentorship.sdk_url, mentorship.created_at] as const]
        : [],
    ),
  );

  const conversations = sortNoticeConversations(
    toNoticeConversations(
      inputs.channels,
      currentUserId,
      mentorshipByUrl,
      buildMenteeAvatarByUrl(inputs.mentorships),
      behavior.computesNeedsResponse,
      { now, startedAtByUrl },
    ),
    inputs.workspace,
  );

  const view = ((): NoticeView => {
    switch (behavior.view) {
      case "flat":
        return { kind: "flat", conversations };
      case "grouped": {
        const internalByUrl = new Map(
          inputs.internalOpsChannels.map((c) => [c.sdk_url, c]),
        );
        const supportConversations = conversations.map((conversation) => ({
          ...conversation,
          internalChannelId: internalByUrl.get(conversation.id)?.id ?? null,
        }));
        const groups = groupSupportConversations(supportConversations, {
          internalByUrl,
          currentUserId,
          isCurrentUserMentor,
          activeSupervisorId,
        });
        return { kind: "grouped", groups, conversations: supportConversations };
      }
      default: {
        const exhaustiveCheck: never = behavior.view;
        return exhaustiveCheck;
      }
    }
  })();

  const loading = behavior.fetchesInternalOps
    ? inputs.channelsLoading || inputs.internalLoading
    : inputs.channelsLoading;

  // Support-only; a supervisor-lookup failure is auxiliary metadata and must
  // not blank the whole list.
  const error =
    inputs.channelsError ??
    (behavior.fetchesInternalOps ? inputs.internalError : null);

  return { view, loading, error };
}
